import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CalendarCheck, Loader2, Check, X, Clock, CalendarDays, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATUSES = [["Present", "bg-emerald-500", Check], ["Absent", "bg-rose-500", X], ["Leave", "bg-amber-500", Clock], ["Late", "bg-sky-500", Clock], ["Half Day", "bg-violet-500", Clock]];

export default function Attendance() {
  const { user } = useAuth();
  const [type, setType] = useState("student");
  const [classId, setClassId] = useState("");
  const [section, setSection] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [classes, setClasses] = useState([]);
  const [people, setPeople] = useState([]);
  const [marks, setMarks] = useState({});
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [monthMarks, setMonthMarks] = useState({});
  const [monthLoading, setMonthLoading] = useState(false);
  const [holidayDocs, setHolidayDocs] = useState({});

  const pad2 = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const isFuture = (dateStr) => dateStr > todayStr;
  const dstr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const [calOpen, setCalOpen] = useState(false);
  const [calDate, setCalDate] = useState(new Date());
  const [calDocs, setCalDocs] = useState({});
  const [calAudience, setCalAudience] = useState(type);
  const [namingDate, setNamingDate] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const loadCalDocs = async (d, aud) => {
    const mo = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
    try { const r = await api.get("/holidays", { params: { month: mo, audience: aud } }); const m = {}; (r.data || []).forEach((h) => { m[h.date] = { name: h.name || "Holiday", working: !!h.working }; }); setCalDocs(m); } catch { /* ignore */ }
  };
  useEffect(() => { if (calOpen) loadCalDocs(calDate, calAudience); }, [calOpen, calDate, calAudience]);
  const applyDoc = (ds, doc) => {
    setCalDocs((m) => { const n = { ...m }; if (doc) n[ds] = doc; else delete n[ds]; return n; });
    if (ds.slice(0, 7) === month && calAudience === type) setHolidayDocs((m) => { const n = { ...m }; if (doc) n[ds] = doc; else delete n[ds]; return n; });
  };
  const saveHolidayDoc = async (ds, name, working) => {
    try { await api.post("/holidays", { date: ds, name, working, audience: calAudience }); applyDoc(ds, { name, working }); toast.success(working ? "Marked as working day" : "Holiday set"); }
    catch { toast.error("Failed"); }
  };
  const deleteHolidayDoc = async (ds) => {
    try { await api.delete(`/holidays/${ds}`, { params: { audience: calAudience } }); applyDoc(ds, null); toast.success("Reverted"); }
    catch { toast.error("Failed"); }
  };
  const onCalDayClick = (d) => {
    if (!d) return;
    const ds = dstr(d);
    const doc = calDocs[ds];
    const sunday = d.getDay() === 0;
    if (doc) { deleteHolidayDoc(ds); return; }        // has an override (holiday or working) -> clear it
    if (sunday) { saveHolidayDoc(ds, "Working Day", true); return; } // default Sunday holiday -> make working
    setNamingDate(ds); setNameInput("");             // normal weekday -> ask holiday name
  };
  const confirmName = () => { if (!namingDate) return; saveHolidayDoc(namingDate, nameInput.trim() || "Holiday", false); setNamingDate(null); setNameInput(""); };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const normalizeDate = (s) => {
    s = s.trim();
    let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
    m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (m) return `${m[3]}-${pad2(m[2])}-${pad2(m[1])}`;
    return null;
  };
  const parseLine = (line) => {
    const raw = line.trim();
    if (!raw) return null;
    let dateStr, name;
    if (raw.includes(",")) { const i = raw.indexOf(","); dateStr = raw.slice(0, i); name = raw.slice(i + 1).trim(); }
    else if (raw.includes("\t")) { const i = raw.indexOf("\t"); dateStr = raw.slice(0, i); name = raw.slice(i + 1).trim(); }
    else { const mm = raw.match(/^(\S+)\s+(.*)$/); if (mm) { dateStr = mm[1]; name = mm[2].trim(); } else { dateStr = raw; name = ""; } }
    const iso = normalizeDate(dateStr);
    if (!iso) return { error: raw };
    return { date: iso, name: name || "Holiday" };
  };
  const runBulkImport = async () => {
    const items = [], errs = [];
    bulkText.split("\n").forEach((l) => { const r = parseLine(l); if (!r) return; if (r.error) errs.push(r.error); else items.push(r); });
    if (!items.length) { toast.error("No valid dates found. Use one per line: 2026-08-15, Independence Day"); return; }
    try {
      const res = await api.post("/holidays/bulk", { items, audience: calAudience });
      toast.success(`Imported ${res.data.count} holiday(s)${errs.length ? ` · ${errs.length} line(s) skipped` : ""}`);
      setBulkText(""); setBulkOpen(false);
      loadCalDocs(calDate, calAudience); if (showRegister) loadHolidays();
    } catch { toast.error("Import failed"); }
  };

  const [staffStatuses, setStaffStatuses] = useState(["Present", "Absent", "Leave", "Late", "Half Day", "Holiday"]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); api.get("/settings").then((r) => { const s = r.data?.staff_attendance_statuses; if (s && s.length) setStaffStatuses(s); }).catch(() => {}); }, []);
  const classNames = [...new Set(classes.map((c) => (c.name || "").trim()).filter(Boolean))];
  const sectionsForClass = [...new Set(classes.filter((c) => (c.name || "").trim() === classId).map((c) => (c.section || "").trim()).filter(Boolean))];

  const load = async () => {
    setLoading(true);
    try {
      let list = [];
      if (type === "student") {
        if (!classId) { setPeople([]); setLoading(false); return; }
        const r = await api.get("/students", { params: { class_name: classId, section: section || undefined, limit: 500 } });
        list = r.data.students.map((s) => ({ id: s.id, name: s.name, sub: s.admission_number, father: s.father_name, roll: s.roll_number, section: s.section, cid: s.class_id }));
      } else {
        const r = await api.get("/staff");
        list = r.data.map((s) => ({ id: s.id, name: s.name, sub: s.role }));
      }
      setPeople(list);
      const att = await api.get("/attendance", { params: { date, type, class_name: type === "student" ? classId : undefined } });
      const m = {};
      list.forEach((p) => { m[p.id] = att.data[p.id]?.status || "Present"; });
      setMarks(m);
      if (type === "student" && classId) {
        const sum = await api.get("/attendance/summary", { params: { date, class_name: classId } });
        setSummary(sum.data || {});
      } else setSummary({});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [type, classId, section, date]);

  const save = async () => {
    if (isFuture(date)) { toast.error("Cannot mark attendance for a future date"); return; }
    const records = people.map((p) => ({ person_id: p.id, status: marks[p.id] || "Present", class_id: p.cid }));
    try {
      await api.post("/attendance", { date, type, class_id: null, records });
      toast.success("Attendance saved");
      if (type === "student" && classId) {
        const sum = await api.get("/attendance/summary", { params: { date, class_name: classId } });
        setSummary(sum.data || {});
      }
    } catch {
      toast.error("Failed to save attendance");
    }
  };

  const markAllPresent = () => {
    const m = {};
    people.forEach((p) => { m[p.id] = "Present"; });
    setMarks(m);
    toast.success("All marked Present — flip any absentees, then Save");
  };

  const month = date.slice(0, 7);
  const daysInMonth = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const loadMonth = async () => {
    if (type === "student" && !classId) { setMonthMarks({}); return; }
    setMonthLoading(true);
    try {
      const r = await api.get("/attendance/month", { params: { month, type, class_name: type === "student" ? classId : undefined } });
      setMonthMarks(r.data || {});
    } finally { setMonthLoading(false); }
  };
  const loadHolidays = async () => {
    try { const r = await api.get("/holidays", { params: { month, audience: type } }); const m = {}; (r.data || []).forEach((h) => { m[h.date] = { name: h.name || "Holiday", working: !!h.working }; }); setHolidayDocs(m); } catch { /* ignore */ }
  };
  useEffect(() => { if (showRegister) { loadMonth(); loadHolidays(); } }, [showRegister, month, type, classId, section, people.length]);

  const isSunday = (dateStr) => new Date(`${dateStr}T00:00:00`).getDay() === 0;
  const holidaysEnabled = type !== "staff" || staffStatuses.includes("Holiday");
  const isHolidayDate = (dateStr) => { if (!holidaysEnabled) return false; const d = holidayDocs[dateStr]; if (d) return !d.working; return isSunday(dateStr); };
  const isCustomHoliday = (dateStr) => { const d = holidayDocs[dateStr]; return !!(d && !d.working); };
  const holidayName = (dateStr) => { const d = holidayDocs[dateStr]; if (d && !d.working) return d.name || "Holiday"; if (!d && isSunday(dateStr)) return "Sunday"; return ""; };
  const removeHoliday = async (dateStr) => {
    if (!window.confirm("Remove this holiday?")) return;
    try { await api.delete(`/holidays/${dateStr}`, { params: { audience: type } }); setHolidayDocs((m) => { const n = { ...m }; delete n[dateStr]; return n; }); toast.success("Holiday removed"); }
    catch { toast.error("Failed"); }
  };

  const activeStatuses = type === "staff" ? STATUSES.filter(([s]) => staffStatuses.includes(s)) : STATUSES;
  const statusOn = (s) => type !== "staff" || staffStatuses.includes(s);
  const cycleHint = ["Present", "Absent", "Leave", "Late", "Half Day"].filter((s) => statusOn(s)).map((s) => ({ Present: "P", Absent: "A", Leave: "L", Late: "LT", "Half Day": "HF" }[s])).join(" → ");
  const cycleList = (type === "staff" ? ["Present", "Absent", "Leave", "Late", "Half Day"].filter((s) => staffStatuses.includes(s)) : ["Present", "Absent", "Leave", "Late", "Half Day"]);
  const CYCLE = { "": cycleList[0] || "Present" };
  cycleList.forEach((s, i) => { CYCLE[s] = cycleList[(i + 1) % cycleList.length]; });
  const LETTER = { Present: "P", Absent: "A", Leave: "L", Late: "LT", "Half Day": "HF" };
  const LETTER_CLR = { Present: "bg-emerald-500 text-white", Absent: "bg-rose-500 text-white", Leave: "bg-amber-500 text-white", Late: "bg-sky-500 text-white", "Half Day": "bg-violet-500 text-white" };

  const cycleCell = async (personId, day) => {
    if (!canEdit) return;
    const dd = String(day).padStart(2, "0");
    if (isFuture(`${month}-${dd}`)) { toast.error("Cannot mark attendance for a future date"); return; }
    const cur = monthMarks[personId]?.[dd] || "";
    const next = CYCLE[cur] ?? (cycleList[0] || "Present");
    setMonthMarks((mm) => ({ ...mm, [personId]: { ...(mm[personId] || {}), [dd]: next } }));
    try {
      await api.post("/attendance", { date: `${month}-${dd}`, type, class_id: null, records: [{ person_id: personId, status: next, class_id: people.find((p) => p.id === personId)?.cid }] });
    } catch {
      toast.error("Failed to save");
      setMonthMarks((mm) => ({ ...mm, [personId]: { ...(mm[personId] || {}), [dd]: cur } }));
    }
  };

  const canEdit = hasPerm(user, "edit_attendance");
  const counts = STATUSES.map(([s]) => people.filter((p) => marks[p.id] === s).length);

  const fillDay = async (day) => {
    if (!canEdit) return;
    const dd = String(day).padStart(2, "0");
    const dateStr = `${month}-${dd}`;
    if (isHolidayDate(dateStr)) return;
    if (isFuture(dateStr)) { toast.error("Cannot mark attendance for a future date"); return; }
    const prev = {};
    people.forEach((p) => { prev[p.id] = monthMarks[p.id]?.[dd] || ""; });
    setMonthMarks((mm) => {
      const next = { ...mm };
      people.forEach((p) => { next[p.id] = { ...(next[p.id] || {}), [dd]: "Present" }; });
      return next;
    });
    try {
      await api.post("/attendance", { date: dateStr, type, class_id: null, records: people.map((p) => ({ person_id: p.id, status: "Present", class_id: p.cid })) });
      toast.success(`Day ${dd} marked Present`);
    } catch {
      toast.error("Failed to save");
      setMonthMarks((mm) => { const next = { ...mm }; people.forEach((p) => { next[p.id] = { ...(next[p.id] || {}), [dd]: prev[p.id] }; }); return next; });
    }
  };

  const fillRow = async (personId) => {
    if (!canEdit) return;
    const targetDays = days.filter((d) => { const ds = `${month}-${String(d).padStart(2, "0")}`; return !isHolidayDate(ds) && !isFuture(ds); });
    if (!targetDays.length) { toast.error("No past/current days to mark"); return; }
    const prev = { ...(monthMarks[personId] || {}) };
    setMonthMarks((mm) => {
      const row = { ...(mm[personId] || {}) };
      targetDays.forEach((d) => { row[String(d).padStart(2, "0")] = "Present"; });
      return { ...mm, [personId]: row };
    });
    const results = await Promise.allSettled(
      targetDays.map((d) => api.post("/attendance", { date: `${month}-${String(d).padStart(2, "0")}`, type, class_id: null, records: [{ person_id: personId, status: "Present", class_id: people.find((p) => p.id === personId)?.cid }] }))
    );
    const failedDays = targetDays.filter((_, i) => results[i].status === "rejected");
    if (!failedDays.length) {
      toast.success("Whole row marked Present");
      return;
    }
    setMonthMarks((mm) => {
      const row = { ...(mm[personId] || {}) };
      failedDays.forEach((d) => { const dd = String(d).padStart(2, "0"); if (prev[dd]) row[dd] = prev[dd]; else delete row[dd]; });
      return { ...mm, [personId]: row };
    });
    toast.error(`Saved, but ${failedDays.length} day(s) failed — please retry`);
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-extrabold text-slate-900">Attendance</h1>
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-end">
        <div><Label>Type</Label><Select value={type} onValueChange={setType}><SelectTrigger className="w-32" data-testid="att-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="student">Student</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent></Select></div>
        {type === "student" && <div><Label>Class</Label><Select value={classId} onValueChange={(v) => { setClassId(v); setSection(""); }}><SelectTrigger className="w-40" data-testid="att-class"><SelectValue placeholder="Select class" /></SelectTrigger><SelectContent>{classNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}</SelectContent></Select></div>}
        {type === "student" && classId && <div><Label>Section</Label><Select value={section || "__all__"} onValueChange={(v) => setSection(v === "__all__" ? "" : v)}><SelectTrigger className="w-36" data-testid="att-section"><SelectValue placeholder="All sections" /></SelectTrigger><SelectContent><SelectItem value="__all__">All sections</SelectItem>{sectionsForClass.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>}
        <div><Label>Date</Label><Input type="date" max={todayStr} value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" /></div>
        <Button variant={showRegister ? "default" : "outline"} onClick={() => setShowRegister((v) => !v)} className="gap-1.5" data-testid="attendance-register-btn"><CalendarCheck size={16} />{showRegister ? "Daily View" : "Attendance Register"}</Button>
        {canEdit && (
          <Dialog open={calOpen} onOpenChange={(o) => { setCalOpen(o); if (o) setCalAudience(type); else { setNamingDate(null); setNameInput(""); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-1.5" data-testid="holiday-calendar-btn"><CalendarDays size={16} />Holiday Calendar</Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-fit max-h-[92vh] overflow-y-auto" data-testid="holiday-calendar-dialog">
              <DialogHeader><DialogTitle className="text-2xl">Holiday Calendar — {calAudience === "staff" ? "Staff" : "Students"}</DialogTitle></DialogHeader>
              <div className="flex gap-2" data-testid="holiday-audience-toggle">
                <Button size="sm" variant={calAudience === "student" ? "default" : "outline"} onClick={() => setCalAudience("student")} data-testid="holiday-aud-student">Students</Button>
                <Button size="sm" variant={calAudience === "staff" ? "default" : "outline"} onClick={() => setCalAudience("staff")} data-testid="holiday-aud-staff">Staff</Button>
              </div>
              <p className="text-sm text-slate-500 max-w-full sm:max-w-md">Setting holidays for <b>{calAudience === "staff" ? "Staff" : "Students"}</b>. Click a weekday to name it a holiday (violet). Click a Sunday to turn it into a working day (green); click again to restore it.</p>
              <Calendar mode="single" month={calDate} onMonthChange={setCalDate} onDayClick={onCalDayClick}
                className="p-1"
                classNames={{
                  caption_label: "text-lg font-semibold",
                  nav_button: "h-9 w-9 bg-transparent p-0 opacity-60 hover:opacity-100 border rounded-md inline-flex items-center justify-center",
                  head_cell: "text-slate-500 rounded-md w-9 sm:w-10 font-medium text-sm",
                  cell: "relative p-0 text-center text-base focus-within:relative focus-within:z-20 [&:has([aria-selected])]:rounded-md",
                  day: "h-8 w-9 sm:h-10 sm:w-10 p-0 font-medium text-sm sm:text-base rounded-md hover:bg-slate-100 inline-flex items-center justify-center transition-colors aria-selected:opacity-100",
                }}
                modifiers={{
                  holiday: (d) => { const doc = calDocs[dstr(d)]; if (doc) return !doc.working; return d.getDay() === 0; },
                  workday: (d) => { const doc = calDocs[dstr(d)]; return !!(doc && doc.working); },
                }}
                modifiersClassNames={{ holiday: "bg-violet-500 text-white rounded-md hover:bg-violet-600 hover:text-white", workday: "bg-emerald-100 text-emerald-700 rounded-md hover:bg-emerald-200" }}
                data-testid="holiday-calendar" />
              {namingDate && (
                <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 flex items-end gap-2" data-testid="holiday-name-panel">
                  <div className="flex-1"><Label className="text-xs">Holiday name — {namingDate}</Label><Input value={nameInput} onChange={(e) => setNameInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmName()} placeholder="e.g. Independence Day" className="mt-1 h-9" autoFocus data-testid="holiday-name-input" /></div>
                  <Button size="sm" onClick={confirmName} data-testid="holiday-name-save">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => { setNamingDate(null); setNameInput(""); }} data-testid="holiday-name-cancel">Cancel</Button>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto border-t border-slate-100 pt-2 max-w-md">
                {Object.keys(calDocs).length === 0 ? <span className="text-sm text-slate-400">Only Sundays are holidays this month. Click any date to change.</span> :
                  Object.entries(calDocs).sort().map(([d, doc]) => <span key={d} className={`text-xs border rounded px-2 py-1 ${doc.working ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-violet-50 text-violet-700 border-violet-200"}`} data-testid={`cal-holiday-${d}`}>{d} · {doc.working ? "Working day" : doc.name}</span>)}
              </div>
              <div className="border-t border-slate-100 pt-2 max-w-sm">
                <button type="button" className="text-xs font-medium text-indigo-600 flex items-center gap-1" onClick={() => setBulkOpen((v) => !v)} data-testid="bulk-import-toggle"><Upload size={13} />{bulkOpen ? "Hide bulk import" : "Bulk import holidays for the year"}</button>
                {bulkOpen && (
                  <div className="mt-2 space-y-2" data-testid="bulk-import-panel">
                    <p className="text-[11px] text-slate-500">One holiday per line as <code className="bg-slate-100 px-1 rounded">date, name</code>. Dates can be <b>YYYY-MM-DD</b> or <b>DD/MM/YYYY</b>. Existing dates are updated.</p>
                    <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} placeholder={"2026-01-26, Republic Day\n2026-08-15, Independence Day\n2026-10-02, Gandhi Jayanti"} className="text-xs font-mono" data-testid="bulk-import-textarea" />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setBulkText("")} data-testid="bulk-import-clear">Clear</Button>
                      <Button size="sm" onClick={runBulkImport} className="gap-1.5" data-testid="bulk-import-run"><Upload size={14} />Import</Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
        {!showRegister && canEdit && people.length > 0 && !isFuture(date) && <Button variant="outline" onClick={markAllPresent} className="gap-1.5" data-testid="mark-all-present-btn"><Check size={16} />Mark All Present</Button>}
        {!showRegister && canEdit && people.length > 0 && !isFuture(date) && <Button onClick={save} data-testid="save-attendance-btn">Save Attendance</Button>}
      </div>

      {!showRegister && people.length > 0 && (
        <div className="flex gap-3 text-sm">
          {activeStatuses.map(([s]) => <div key={s} className="px-3 py-1 rounded-full bg-white border border-slate-200"><span className="font-semibold">{people.filter((p) => marks[p.id] === s).length}</span> {s}</div>)}
        </div>
      )}

      {showRegister ? (
        monthLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div> :
        people.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400"><CalendarCheck size={40} className="mx-auto mb-2 opacity-40" />{type === "student" ? "Select a class to view the register." : "No staff found."}</div> : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <span className="font-medium text-slate-600">Register — {new Date(`${month}-01`).toLocaleString("default", { month: "long", year: "numeric" })}</span>
              {statusOn("Present") && <span className="flex items-center gap-1"><span className="w-5 h-5 rounded bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">P</span> Present</span>}
              {statusOn("Absent") && <span className="flex items-center gap-1"><span className="w-5 h-5 rounded bg-rose-500 text-white flex items-center justify-center font-bold text-[10px]">A</span> Absent</span>}
              {statusOn("Leave") && <span className="flex items-center gap-1"><span className="w-5 h-5 rounded bg-amber-500 text-white flex items-center justify-center font-bold text-[10px]">L</span> Leave</span>}
              {statusOn("Late") && <span className="flex items-center gap-1"><span className="w-6 h-5 rounded bg-sky-500 text-white flex items-center justify-center font-bold text-[10px]">LT</span> Late</span>}
              {statusOn("Half Day") && <span className="flex items-center gap-1"><span className="w-6 h-5 rounded bg-violet-600 text-white flex items-center justify-center font-bold text-[10px]">HF</span> Half Day</span>}
              {holidaysEnabled && <span className="flex items-center gap-1"><span className="w-5 h-5 rounded bg-violet-500 text-white flex items-center justify-center font-bold text-[10px]">H</span> Holiday</span>}
              <span className="text-slate-400">Click a cell to cycle {cycleHint} (auto-saves). Late & Half count in Present (Half as ½).</span>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="text-sm border-collapse">
                <thead className="bg-slate-50 text-slate-500 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[180px] border-r border-slate-200">Name</th>
                    {days.map((d) => {
                      const dd = String(d).padStart(2, "0");
                      const dateStr = `${month}-${dd}`;
                      const wd = new Date(`${dateStr}T00:00:00`).toLocaleDateString("default", { weekday: "short" });
                      const holiday = isHolidayDate(dateStr);
                      return <th key={d} title={holiday ? holidayName(dateStr) : ""} className={`px-1 py-2 w-9 text-center font-semibold ${holiday ? "text-violet-500" : ""}`}><div>{d}</div><div className="text-[9px] font-normal text-slate-400">{holiday ? "H" : wd}</div>{canEdit && !holiday && !isFuture(dateStr) && <button onClick={() => fillDay(d)} title={`Mark all Present on ${dd}`} className="mt-0.5 mx-auto w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-500 hover:text-white flex items-center justify-center transition-colors" data-testid={`register-fill-day-${dd}`}><Check size={10} strokeWidth={3} /></button>}</th>;
                    })}
                    <th className="px-2 py-2 text-center border-l border-slate-200" title="Present (incl. Late + ½ Half)">P</th>
                    {statusOn("Leave") && <th className="px-2 py-2 text-center" title="Leave">L</th>}
                    {statusOn("Late") && <th className="px-2 py-2 text-center" title="Late">LT</th>}
                    {statusOn("Half Day") && <th className="px-2 py-2 text-center" title="Half Day">HF</th>}
                    {statusOn("Absent") && <th className="px-2 py-2 text-center font-bold text-rose-600" title="Absent(s) this month">A</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {people.map((p, i) => {
                    const row = monthMarks[p.id] || {};
                    const vals = Object.entries(row).filter(([dd]) => !isHolidayDate(`${month}-${dd}`)).map(([, v]) => v);
                    const cFull = vals.filter((v) => v === "Present").length;
                    const cA = vals.filter((v) => v === "Absent").length;
                    const cL = vals.filter((v) => v === "Leave").length;
                    const cLT = vals.filter((v) => v === "Late").length;
                    const cHF = vals.filter((v) => v === "Half Day").length;
                    const cP = cFull + cLT + cHF * 0.5;
                    return (
                      <tr key={p.id} data-testid={`register-row-${p.id}`}>
                        <td className="px-3 py-1.5 sticky left-0 bg-white z-10 border-r border-slate-200"><div className="flex items-center gap-1.5"><div className="min-w-0 flex-1"><div className="font-medium text-slate-800 truncate">{i + 1}. {p.name}</div>{type === "student" && <><div className="text-[10px] text-slate-400 truncate">Section: {p.section || "—"}</div><div className="text-[10px] text-slate-400 truncate">Father: {p.father || "—"}</div><div className="text-[10px] text-slate-400 truncate">Roll: {p.roll || "—"}</div></>}</div>{canEdit && <button onClick={() => fillRow(p.id)} title="Mark this row Present for the month" className="shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-500 hover:text-white flex items-center justify-center transition-colors" data-testid={`register-fill-row-${p.id}`}><Check size={12} strokeWidth={3} /></button>}</div></td>
                        {days.map((d) => {
                          const dd = String(d).padStart(2, "0");
                          const dateStr = `${month}-${dd}`;
                          const holiday = isHolidayDate(dateStr);
                          const custom = isCustomHoliday(dateStr);
                          const st = row[dd] || "";
                          const future = isFuture(dateStr);
                          if (holiday) {
                            return (
                              <td key={d} className="px-0.5 py-1 text-center">
                                <button disabled={!canEdit || !custom} onClick={() => custom && removeHoliday(dateStr)} title={custom ? `${holidayName(dateStr)} (click to remove)` : holidayName(dateStr)} className="w-7 h-7 rounded text-[11px] font-bold bg-violet-500 text-white" data-testid={`register-cell-${p.id}-${dd}`}>H</button>
                              </td>
                            );
                          }
                          return (
                            <td key={d} className="px-0.5 py-1 text-center">
                              <button disabled={!canEdit || future} onClick={() => cycleCell(p.id, d)} title={future ? "Future date" : ""} className={`w-7 h-7 rounded text-[11px] font-bold transition-colors ${future ? "bg-slate-50 text-slate-200 cursor-not-allowed" : st ? LETTER_CLR[st] : "bg-slate-100 text-slate-300 hover:bg-slate-200"}`} data-testid={`register-cell-${p.id}-${dd}`}>{future ? "" : st ? LETTER[st] : "·"}</button>
                            </td>
                          );
                        })}
                        <td className="px-2 py-1 text-center font-semibold text-emerald-600 border-l border-slate-200 tabular" data-testid={`register-present-${p.id}`}>{cP}</td>
                        {statusOn("Leave") && <td className="px-2 py-1 text-center font-semibold text-amber-600 tabular">{cL}</td>}
                        {statusOn("Late") && <td className="px-2 py-1 text-center font-semibold text-sky-600 tabular" data-testid={`register-late-${p.id}`}>{cLT}</td>}
                        {statusOn("Half Day") && <td className="px-2 py-1 text-center font-semibold text-violet-600 tabular" data-testid={`register-half-${p.id}`}>{cHF}</td>}
                        {statusOn("Absent") && <td className="px-2 py-1 text-center font-bold text-rose-600 tabular" data-testid={`register-absent-${p.id}`}>{cA}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
      loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div> :
        people.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400"><CalendarCheck size={40} className="mx-auto mb-2 opacity-40" />{type === "student" ? "Select a class to mark attendance." : "No staff found."}</div> : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {people.map((p, i) => {
              const sm = summary[p.id] || { month_present: 0, month_absent: 0, total_present: 0, total_absent: 0, total_percent: 0 };
              return (
              <div key={p.id} className="flex items-center justify-between px-4 py-3 gap-4" data-testid={`att-row-${p.id}`}>
                <div className="flex items-center gap-3 min-w-[200px]">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center" data-testid={`att-serial-${p.id}`}>{i + 1}</div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800">{p.name}</div>
                    {type === "student"
                      ? <div className="text-xs text-slate-400">Section: {p.section || "—"} · Father: {p.father || "—"} · Roll No: {p.roll || "—"}</div>
                      : <div className="text-xs text-slate-400">{p.sub}</div>}
                  </div>
                </div>
                {type === "student" && (
                  <div className="hidden md:flex items-center gap-5 text-xs text-slate-500" data-testid={`att-summary-${p.id}`}>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">This Month</div>
                      <div><span className="font-semibold text-emerald-600">{sm.month_present}</span> P · <span className="font-semibold text-rose-500">{sm.month_absent}</span> A</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">Since April</div>
                      <div><span className="font-semibold text-emerald-600">{sm.total_present}</span> P · <span className="font-semibold text-rose-500">{sm.total_absent}</span> A</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">Attendance %</div>
                      <div className={`font-bold ${sm.total_percent >= 75 ? "text-emerald-600" : sm.total_percent >= 50 ? "text-amber-600" : "text-rose-500"}`}>{sm.total_percent}%</div>
                    </div>
                  </div>
                )}
                <div className="flex gap-1">
                  {activeStatuses.map(([s, color]) => (
                    <button key={s} disabled={!canEdit || isFuture(date)} onClick={() => setMarks({ ...marks, [p.id]: s })} title={isFuture(date) ? "Future date" : s} className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs transition-colors ${isFuture(date) ? "bg-slate-50 text-slate-300 cursor-not-allowed" : marks[p.id] === s ? `${color} text-white` : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`} data-testid={`mark-${p.id}-${s}`}>{LETTER[s]}</button>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
